import type { Checker } from "./mapcheck";
import { groundKind, halfX, halfZ, surfaceAt, wallNormal, type Level } from "./level";
import {
  PROP_BOUNCE,
  PROP_GRAVITY,
  PROP_REST,
  WASH_PUSH,
  WASH_PUSH_MAX,
} from "./sim/tuning";

// Light things lying about the park (#151): traffic cones and empty paint
// cans for the power washer's jet to knock about. A map file's `props` list
// places them, standing on the ground at (x, z). The jet pushes any it
// passes through the way it sprays: the first push knocks it over (a
// `knock` event), and it slides (a cone) or rolls (a can) on, slowing on
// the ground, running down slopes, bouncing off walls and box sides. They
// don't stop the skater or the jet (scenery, like the targets). Moved props
// stay where they end up until the map reloads. All on the sim's clock, so
// a run stays deterministic. Sim- and three-free; `render/props.ts` draws
// them.

export type PropKind = "cone" | "can";

/** Each kind's radius, height and how quickly it slows on the ground (m, m, m/s²). */
export const PROP_KINDS: Readonly<Record<PropKind, { r: number; h: number; friction: number }>> = {
  /** A traffic cone: slides on its side. */
  cone: { r: 0.18, h: 0.5, friction: 3 },
  /** An empty paint can: rolls, so it goes further. */
  can: { r: 0.12, h: 0.24, friction: 1.2 },
};

/** At most this many props on a map. */
export const MAX_PROPS = 16;

/** A prop in a map file: its kind and where it stands. */
export interface MapProp {
  kind: PropKind;
  x: number;
  z: number;
}

/** A prop as it is now: where, how it's moving, and whether it's been knocked over. */
export interface PropBody {
  kind: PropKind;
  x: number;
  /** The ground under it. */
  y: number;
  z: number;
  vx: number;
  vz: number;
  /** Knocked over (stays down once it is). */
  down: boolean;
  /** Which way it lies once down: its heading when last pushed (radians, 0 is +z). */
  yaw: number;
  /** How far it's travelled (m): how far a can has rolled. */
  dist: number;
  /** Gone into the sea. */
  sunk: boolean;
}

export function checkProps(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length > MAX_PROPS) {
    c.fail(path, `expected a list of at most ${MAX_PROPS} props`);
    return;
  }
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["kind", "x", "z"], at);
    if (!Object.hasOwn(PROP_KINDS, o.kind as string)) c.fail(`${at}.kind`, `expected one of ${Object.keys(PROP_KINDS).join(", ")}`);
    const inside = { min: -bounds + 0.5, max: bounds - 0.5 };
    c.num(o, "x", at, inside);
    c.num(o, "z", at, inside);
  });
}

/** The level's props standing where the map file puts them, at rest. */
export function placeProps(level: Level): PropBody[] {
  return (level.props ?? []).map((p) => ({
    kind: p.kind,
    x: p.x,
    y: surfaceAt(level, p.x, p.z).h,
    z: p.z,
    vx: 0,
    vz: 0,
    down: false,
    yaw: 0,
    dist: 0,
    sunk: false,
  }));
}

/**
 * Pushes every prop the jet from (x0, y0, z0) to (x1, y1, z1) passes
 * through, along the jet, for `dt`. Returns the indices of props it knocked
 * over just now.
 */
export function pushProps(
  props: PropBody[],
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  dt: number,
): number[] {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const len2 = dx * dx + dz * dz;
  if (len2 < 1e-9) return [];
  const len = Math.sqrt(len2);
  const ux = dx / len;
  const uz = dz / len;
  const knocked: number[] = [];
  props.forEach((p, i) => {
    if (p.sunk) return;
    const k = PROP_KINDS[p.kind];
    // Where along the jet (flat) it comes closest, and how high the jet is there.
    const t = Math.max(0, Math.min(1, ((p.x - x0) * dx + (p.z - z0) * dz) / len2));
    const cx = x0 + dx * t - p.x;
    const cz = z0 + dz * t - p.z;
    const reach = k.r + JET_HALF_WIDTH;
    if (cx * cx + cz * cz > reach * reach) return;
    const jy = y0 + (y1 - y0) * t;
    // Lying down it's only as tall as it is wide.
    const top = p.y + (p.down ? 2 * k.r : k.h) + JET_HALF_WIDTH;
    if (jy < p.y - JET_HALF_WIDTH || jy > top) return;
    p.vx += ux * WASH_PUSH * dt;
    p.vz += uz * WASH_PUSH * dt;
    const s = Math.hypot(p.vx, p.vz);
    if (s > WASH_PUSH_MAX) {
      p.vx *= WASH_PUSH_MAX / s;
      p.vz *= WASH_PUSH_MAX / s;
    }
    p.yaw = Math.atan2(p.vx, p.vz);
    if (!p.down) {
      p.down = true;
      knocked.push(i);
    }
  });
  return knocked;
}

/** The jet's half width, for what it pushes (m). */
const JET_HALF_WIDTH = 0.12;

/** A step in the ground taller than this is a box's side, which a prop bounces off (m). */
const SIDE_STEP = 0.1;

/**
 * Moves the props on `dt`: down slopes, slowing on the ground (sand stops
 * them quicker), off walls and box sides. One at rest on the flat stays put.
 */
export function stepProps(level: Level, props: PropBody[], dt: number): void {
  for (const p of props) {
    if (p.sunk) continue;
    const s = surfaceAt(level, p.x, p.z);
    const slope = Math.hypot(s.gx, s.gz);
    let speed = Math.hypot(p.vx, p.vz);
    // Standing up, it's not going anywhere until it's pushed.
    if (!p.down || (speed === 0 && slope < PROP_REST)) continue;
    p.vx -= s.gx * PROP_GRAVITY * dt;
    p.vz -= s.gz * PROP_GRAVITY * dt;
    speed = Math.hypot(p.vx, p.vz);
    const sand = level.edges !== undefined && groundKind(level, p.x, p.z) === "sand";
    const slow = PROP_KINDS[p.kind].friction * (sand ? 4 : 1) * dt;
    if (speed <= slow) {
      p.vx = 0;
      p.vz = 0;
      continue;
    }
    p.vx -= (p.vx / speed) * slow;
    p.vz -= (p.vz / speed) * slow;
    move(level, p, dt);
  }
}

function move(level: Level, p: PropBody, dt: number): void {
  const nx = p.x + p.vx * dt;
  const nz = p.z + p.vz * dt;
  const hx = halfX(level);
  const hz = halfZ(level);
  const walled = (side: string) => !level.edges?.some((e) => e.side === side);
  // The fence: bounce back off it.
  if ((nx > hx && walled("east")) || (nx < -hx && walled("west"))) {
    p.vx = -p.vx * PROP_BOUNCE;
    p.vz *= PROP_BOUNCE;
    return;
  }
  if ((nz > hz && walled("south")) || (nz < -hz && walled("north"))) {
    p.vz = -p.vz * PROP_BOUNCE;
    p.vx *= PROP_BOUNCE;
    return;
  }
  const there = surfaceAt(level, nx, nz).h;
  if (there - p.y > SIDE_STEP) {
    // A box's side: bounce off it the way it faces.
    const n = wallNormal(level, nx, nz, p.x, p.z);
    const into = p.vx * n.x + p.vz * n.z;
    if (into < 0) {
      p.vx = (p.vx - 2 * into * n.x) * PROP_BOUNCE;
      p.vz = (p.vz - 2 * into * n.z) * PROP_BOUNCE;
    }
    return;
  }
  p.dist += Math.hypot(nx - p.x, nz - p.z);
  p.x = nx;
  p.z = nz;
  p.y = there;
  if (level.edges !== undefined && groundKind(level, nx, nz) === "water") {
    p.sunk = true;
    p.vx = 0;
    p.vz = 0;
  }
}
